import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild, ViewContainerRef, ViewEncapsulation } from "@angular/core";
import { FormArray, FormBuilder, FormGroup } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { NgxSpinnerService } from "ngx-spinner";
import { forkJoin, Subject, of, Observable } from "rxjs";
import { takeUntil, catchError, mergeMap} from "rxjs/operators";
import { LibraryImage, LightshowPreviewComponent, SegmentType } from "../lightshow-preview/lightshow-preview.component";
import { EditorMode, LightshowState, SegmentMode } from "../lightshow-preview/lightshow-state";
import { FlashTypes } from "../lightshow-preview/operation-types";
import { LightShowOperation } from "../lightshow-preview/Operations/lightshow-operation";
import { SparkleOperation } from "../lightshow-preview/Operations/sparkle-operation";
import { StrobeOperation } from "../lightshow-preview/Operations/strobe-operation";
import { TorchOperation } from "../lightshow-preview/Operations/torch-operation";
import { LightShowSegment } from "../lightshow-preview/Segments/lightshow-segment";
import { Mp3PlayerComponent } from "../mp3-slider/mp3-player.component";
import { DialogService } from "../_services/dialog.service";
import { FileDownloadService } from "../_services/file-download.service";
import { LightShowFormData, LightShowService } from "../_services/light-show.service";
import { LightShowTrackService } from "../_services/track.service";


@Component({
  selector: 'app-lightshow-public-preview',
  templateUrl: './lightshow-public-preview.component.html',
  styleUrls: ['./lightshow-public-preview.component.css', '../lightshow-preview/lightshow-preview.component.css', '../portal/portal.component.css'],
  encapsulation: ViewEncapsulation.None
})

export class LightShowPublicPreviewComponent implements OnInit, OnDestroy, AfterViewInit {

  public state = new LightshowState();
  public colorToDisplay = 'black';
  public colorSegmentOpacity = 1;
  public imageSegmentOpacity = 1;
  public selectedColorSegment: LightShowSegment;
  public selectedImageSegment: LightShowSegment;
  public currentSoundEffect = new Audio();
  public editorMode = EditorMode.SCREEN;
  public segmentMode: SegmentMode = SegmentMode.IMAGE;
  public trackOperations: LightShowOperation[] = [];
  public originalTrackOperations: any[] = [];
  public venueId: number;
  public shouldSyncWithSlider = true;
  public colorCounter = -1;
  public selectedSoundEffectSegment: LightShowSegment;
  public lightShowSizeColor = 'rgba(0, 255, 0, 255)';
  public lightShowSize = 0;
  public currentOperation = null;
  public previousOperation = null;
  public colorPending: boolean;
  public previousStrobePosition = 0;
  public strobeCounter = 0;
  public minFadeIn = 200;

  public trackUrl: string;
  public trackFile: File;
  public trackDuration: string;
  public publicGuid: string;
  public id: number;
  public form: FormGroup = null;
  public lightShowTypeCode: string;

  public get segments(): LightShowSegment[] {
    return this.form.get('customModeData.segments').value;
  }

  public get imageLibrary(): LibraryImage[] {
    return this.form.get('customModeData.imageLibrary').value;
  }

  public set selectedSegment(segGroup: FormGroup) {
    this.form.markAsDirty();
    const idx = (this.form.get('customModeData.segments') as FormArray).controls.indexOf(segGroup);
    this.form.get('customModeData').patchValue({ selectedSegmentIndex: idx });
  }

  public get selectedSegmentValue(): LightShowSegment {
    if (this.selectedSegment) {
      const val = this.selectedSegment.value;
      LightshowPreviewComponent.initSegment(val);
      return val;
    }
  }

  public get showFullScreenPreview() {
    return this.lightshowService.showFullScreenPreview;
  }

  get preview() {
    return this.previewRef.nativeElement;
  }

  @ViewChild('preview', { static: false })
  previewRef: ElementRef;

  private _player: Mp3PlayerComponent;

  private get player(): Mp3PlayerComponent {

    return this._player;
  }
  
  @ViewChild('player', { static: false })
  private set player(value: Mp3PlayerComponent) {

    if (value) {

      this._player = value;

      this._onPlayerSet$.next();
      this._onPlayerSet$.complete();
    }
  };

  private _onPlayerSet$ = new Subject<void>();
  private _onDestroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private lightshowService: LightShowService,
    private trackService: LightShowTrackService,
    private spinner: NgxSpinnerService,
    private dialogService: DialogService,
    private viewRef: ViewContainerRef,
    private fb: FormBuilder,
    private changeDetector: ChangeDetectorRef,
    private fileService: FileDownloadService
  ){}

  ngOnInit(): void {
    
    this.spinner.show();

    this.route.params
      .subscribe(
        params => {

          this.publicGuid = params['guid'];

          this.lightshowService.getShareableLightShow(this.publicGuid)
            .pipe(mergeMap(lsData => forkJoin([of(lsData), this.fileService.downloadFile(lsData.fileDownloadUrl)])))
            .pipe(mergeMap(data => forkJoin([of(data[0]), of(data[1]), this.trackService.getDurationString(data[1])])))
            .subscribe(
              data => {

                this.spinner.hide();

                this.trackUrl = data[0].fileDownloadUrl;
                this.trackFile = data[1];
                this.trackDuration = data[2];

                this.setFormData(data[0]);

                this.venueId = data[0]?.venueId ? data[0].venueId : 0;
                this.loadTrackData();
                
                this.lightshowService.openFullScreen$
                  .pipe(takeUntil(this._onDestroy$))
                  .subscribe(() => {
                    this.lightshowService.showFullScreenPreview = true;

                    this.changeDetector.detectChanges();
                  });

                this.lightshowService.openFullScreen$.next();
              },
              error => {

                this.spinner.hide();
                this.dialogService.alert('Uh oh!', "We couldn't retrieve a preview for that lightshow.", this.viewRef);
              }
            );
        }
      )
  }

  ngAfterViewInit(): void {
    
    this._onPlayerSet$
      .subscribe(
        _ => {

          this.player.playbackStarted.pipe(takeUntil(this._onDestroy$)).subscribe(_ => this.state.isPlaying = true);
          this.player.playbackPaused.pipe(takeUntil(this._onDestroy$)).subscribe(_ => { this.state.isPlaying = false; this.currentSoundEffect.pause(); });
          this.player.playbackStopped.pipe(takeUntil(this._onDestroy$)).subscribe(_ => { this.state.isPlaying = false; this.currentSoundEffect.pause(); });
          this.player.playPositionChanged
            .pipe(takeUntil(this._onDestroy$))
            .subscribe(pos => {
              this.shouldSyncWithSlider = true;
              this.seekPosition(pos);
              this.animateLightshow(pos);
            });
        }
      );
  }

  setFormData(data: LightShowFormData) {

    const assets = JSON.parse(data.assetData);

    assets.customModeData.segments = assets.customModeData.segments.map(
      s => {

        s.position += 2000;
        return s;
      }
    );

    this.lightShowTypeCode = data.typeCode;

    this.form = this.fb.group({
      cropEndPercent: assets.cropEndPercent,
      upnId: assets.upnId || 0,
      mode: assets.mode,
      customModeData: this.fb.group({
        selectedSegmentIndex: assets.customModeData.selectedSegmentIndex || 0,
        segments: this.fb.array(assets.customModeData.segments.map(s => LightshowPreviewComponent.segmentToGroup(this.fb, s))),
        imageLibrary: this.fb.array(assets.customModeData.imageLibrary ? assets.customModeData.imageLibrary : []),
        colorsLibrary: this.fb.array(assets.customModeData.colorsLibrary ? assets.customModeData.colorsLibrary : []),
      }),
      lightShowJson: assets.lightShowJson
    });
  }

  private loadTrackData() {

    this.originalTrackOperations = [];

    if (!this.form) {
      return;
    }

    // if asset data is found, use light show data from there, otherwise use the default track lightshow
    const assetLightShowJson = this.form.get('lightShowJson');
    let operationsData = JSON.parse(assetLightShowJson.value);

    // parse the lightshow json into operations
    operationsData.forEach((element, index) => {

      switch (+element.type) {
        case FlashTypes.torchOn:
          this.originalTrackOperations.push(new TorchOperation(+element.time + 2000, element.duration * 1000, element.sectionList, true));
        break;
        case FlashTypes.strobeOn:
          this.originalTrackOperations.push(new StrobeOperation(+element.time + 2000, element.duration * 1000, element.sectionList, true));
        break;
        case FlashTypes.sparkleSteady:
        case FlashTypes.sparkleRampUp:
        case FlashTypes.sparkleRampDown:
          this.originalTrackOperations.push(new SparkleOperation(+element.type + 2000, +element.time, element.duration * 1000, element.sectionList, true));
        break;
      }
    });

    this.trackOperations = this.originalTrackOperations;

  }

  animateLightshow(position: number) {
    // get an array from all segments that contain the music player slider
    const selectedSegments = this.segments.filter(seg => {
      const startPosition = seg.position;
      const endPosition = seg.position + seg.duration;
      if (position >= startPosition && position <= endPosition) {
        return true;
      }
    });

    // assign the segments to their appropriate selector
    selectedSegments.forEach(seg => {
      if (this.getSegmentType(seg) === SegmentType.SoundEffect) {
        this.selectedSoundEffectSegment = seg;
      } else if (this.getSegmentType(seg) === SegmentType.Image) {
        this.selectedImageSegment = seg;
      } else if (this.getSegmentType(seg) === SegmentType.SmartColor) {
        this.selectedColorSegment = seg;
      } else if (this.getSegmentType(seg) === SegmentType.SolidColor) {
        this.selectedColorSegment = seg;
      } else if (this.getSegmentType(seg) === SegmentType.ScreenStrobe){
        this.selectedColorSegment = seg;
      }
    });

    // clean up any wrongly selected ones. This should be refactored
    if (!selectedSegments.includes(this.selectedSoundEffectSegment)) {
      this.selectedSoundEffectSegment = null;
      if (!this.currentSoundEffect.paused && this.currentSoundEffect.readyState > 2 && !this.currentSoundEffect.ended) {
        this.currentSoundEffect.pause();
      }
    }

    if (!selectedSegments.includes(this.selectedImageSegment)) {
      this.selectedImageSegment = null;
    }

    if (!selectedSegments.includes(this.selectedColorSegment)) {
      this.selectedColorSegment = null;
    }

    // set color to display to segments duration / number of colors

    if (this.selectedColorSegment) {

      if (this.getSegmentType(this.selectedColorSegment) === SegmentType.SmartColor) {
        let changeDetector = false;
        const pos = this.player.playPosition;
        const torchOn = this.originalTrackOperations.find(op => op.flashType === FlashTypes.torchOn && op.position <= pos && op.position + op.duration >= pos);
        const strobeOn = this.originalTrackOperations.find(op => op.flashType === FlashTypes.strobeOn && op.position <= pos && op.position + op.duration >= pos);

        // If we have a current operation and the position lies within a torch or strobe operation that is not the same as our current operation...
        if (this.currentOperation && ((torchOn && this.currentOperation?.uniqueId !== torchOn?.uniqueId) || (strobeOn && this.currentOperation?.uniqueId !== strobeOn?.uniqueId))){
          // Set the previous operation to what was our current operation
          this.previousOperation = this.currentOperation;
        } else {
          // Otherwise make sure our previous operation is null, since it's possible the position does not lie within a valid operation
          this.previousOperation = null;
        }
        this.currentOperation = torchOn ? torchOn : strobeOn ? strobeOn : null;

        // If we have a current and previous operation and they're overlapping, we don't try to change colors. changeDetector will end up false
        if (this.currentOperation && this.previousOperation && this.previousOperation.position + this.previousOperation.duration > this.currentOperation.position){
          this.currentOperation = null;
          this.previousOperation = null;
        }

        if (torchOn || strobeOn) {
          // Added logic: no color pending OR we have both a current and previous operation
          if ((!this.colorPending) || (this.previousOperation && this.currentOperation)) {
            changeDetector = true;
          }
          this.colorPending = true;
        } else {
          this.colorPending = false;
        }

        if (changeDetector) {
          // I've initialized colorCounter to start at -1, since we always increment it before using it to access colors
          // We might want to make it clear to the user in some way that colorToDisplay is initalized to black, therefore making the first color of a smartcolor black 
          // will give the appearance that smart color transitions aren't working for the first transition. 
          this.colorCounter += 1;
          if (this.colorCounter >= this.selectedColorSegment.colors.length) {
            this.colorCounter = 0;
          }

          if (this.colorToDisplay === 'black') {
            this.colorToDisplay = this.selectedColorSegment.colors[0];
          } else {
            this.colorToDisplay = this.selectedColorSegment.colors[this.colorCounter];
          }
        }
      } else if (this.getSegmentType(this.selectedColorSegment) === SegmentType.ScreenStrobe){
          const timeElapsed = this.state.playbackPosition - this.previousStrobePosition;

          if (timeElapsed < 0){
            this.previousStrobePosition = 0;
          }

          if (timeElapsed >= 100 || this.previousStrobePosition === 0){
            this.colorToDisplay = this.selectedColorSegment.colors[this.strobeCounter % this.selectedColorSegment.colors.length];
            this.previousStrobePosition = this.state.playbackPosition;
            this.strobeCounter++;
          }
      } else if (this.selectedColorSegment.colors.length === 1) {
        this.colorToDisplay = this.selectedColorSegment.colors[0];
      }

    } else if (this.colorToDisplay !== 'black'){
      this.colorToDisplay = 'black';
    }

    // fade in and fade out logic for color segments
    if (this.selectedColorSegment) {
      const fadein = +this.selectedColorSegment.fadeIn;
      const fadeout = +this.selectedColorSegment.fadeOut;
      const pos = +this.selectedColorSegment.position;
      const dur = +this.selectedColorSegment.duration;

      if (position <= pos + fadein) {
        this.colorSegmentOpacity = (position - pos) / fadein;
      } else if (position >= pos + dur - fadeout) {
        this.colorSegmentOpacity = (position - pos - dur) / -fadeout;
      } else {
        this.colorSegmentOpacity = 1;
      }
    }

    // fade in and fade out logic for image segments
    if (this.selectedImageSegment) {
      const fadein = +this.selectedImageSegment.fadeIn + this.minFadeIn;
      const fadeout = +this.selectedImageSegment.fadeOut;
      const pos = +this.selectedImageSegment.position;
      const dur = +this.selectedImageSegment.duration;
      if (position <= pos + fadein) {
        this.imageSegmentOpacity = (position - pos) / fadein;
      } else if (position >= pos + dur - fadeout) {
        this.imageSegmentOpacity = (position - pos - dur) / - fadeout;
      } else {
        this.imageSegmentOpacity = 1;
      }

      if (!this.selectedColorSegment) {
        this.colorToDisplay = this.selectedImageSegment.imageBackgroundColor;
      }
    }

    // handle sound effects
    if (this.selectedSoundEffectSegment && this.state.isPlaying) {
      if (this.currentSoundEffect.src !== this.selectedSoundEffectSegment.audioUrl) {
        this.currentSoundEffect.src = this.selectedSoundEffectSegment.audioUrl;
      }
      const playPromise = this.currentSoundEffect.play();
      if (playPromise !== undefined) {
        playPromise.then(_ => {
          const pos = +this.selectedSoundEffectSegment.position;
          const time = (position - pos) / 1000;
          const MAXIMUM_DIFFERENCE_BEFORE_SYNCING = 0.2;
          if (this.shouldSyncWithSlider && Math.abs(time - this.currentSoundEffect.currentTime) > MAXIMUM_DIFFERENCE_BEFORE_SYNCING) {
            this.currentSoundEffect.currentTime = time;
            this.shouldSyncWithSlider = false;
          }
        })
          .catch(_ => _);
      }
    }
  }

  getSegmentType(seg: LightShowSegment): SegmentType {
    if (seg && seg.audioUrl && seg.audioName) {
      return SegmentType.SoundEffect;
    } else if (seg && seg.colors.length > 1 && seg.segmentType === 5) {
      return SegmentType.SmartColor;
    } else if (seg && seg.colors.length === 1 && seg.segmentType === 5) {
      return SegmentType.SolidColor;
    } else if (seg && seg.segmentType === 6) {
      return SegmentType.ScreenStrobe;
    }else if (seg && seg.imageUrl && (seg.segmentType === 16 || seg.segmentType === 31)) {
      return SegmentType.Image;
    }

    return SegmentType.Unknown;
  }

  seekPosition(position: number) {
    // this.currentTime = position;
    this.state.seekPosition(position, this.combineOperations());
    // I *think* this is a good idea. If the seek position is dragged or end up back at the beginning,
    // The colorToDisplay and color counter should be reset. 
    if (position === 0){
      this.colorToDisplay = 'black';
      this.colorCounter = -1
    }
  }

  updateLightShowSize() {
    let lightShowSizeBytes = 0;
    this.imageLibrary.forEach(libImage => {
      const segments = this.segments.filter(s => s.imageName === libImage.name);
      if (segments.length > 0) {
        lightShowSizeBytes += libImage.size ? libImage.size : 0;
      }
    });

    const percent = lightShowSizeBytes / (20 * 1024 * 1024);

    if (percent < .5) {
      this.lightShowSizeColor = `rgba(${510 * percent}, 255, 0, 255)`;
    } else if (percent === .5) {
      this.lightShowSizeColor = `rgba(255, 255, 0, 255)`;
    } else if (percent > .5) {
      this.lightShowSizeColor = `rgba(255, ${510 * (1 - percent)}, 0, 255)`;
    }

    this.lightShowSize = Math.floor(lightShowSizeBytes / (1024 * 1024));

  }

  private combineOperations(): LightShowOperation[] {
    let operations = [];
    // add track operations
    operations = operations.concat(this.trackOperations);
    // add user defined operations

    this.segments.filter(seg => !seg.hidden).forEach(s => LightshowPreviewComponent.initSegment(s).addOperations(operations));
    return operations.sort((o1, o2) => o1.position - o2.position);
  }

  ngOnDestroy(): void {
    
    this._onDestroy$.complete();
  }
}